<script lang="ts" setup>
const slots = useSlots();
const emit = defineEmits<{
  (e: 'update:modelValue', p: string | number | null): void;
  (e: 'enter'): void;
  (e: 'clickLeft'): void;
  (e: 'clickRight'): void;
  (e: 'focus'): void;
  (e: 'blur'): void;
}>();

interface Props {
  autofocus?: boolean;
  disabled?: boolean;
  error?: string;
  errors?: string[];
  label?: string;
  maxlength?: number;
  minlength?: number;
  modelValue?: string | number | undefined | null;
  name?: string;
  placeholder?: string;
  required?: boolean;
  type?: 'text' | 'password' | 'email' | 'tel' | 'number';
  isDarkBackground?: boolean;
  min?: number;
  max?: number;
  step?: number;
}

const props = withDefaults(defineProps<Props>(), {
  autofocus: undefined,
  disabled: undefined,
  error: undefined,
  errors: () => [],
  label: undefined,
  maxlength: undefined,
  minlength: undefined,
  name: undefined,
  placeholder: undefined,
  required: false,
  type: 'text',
  validator: undefined,
  modelValue: undefined,
  min: undefined,
  max: undefined,
  step: undefined,
});

const inputEl = useTemplateRef<HTMLInputElement>('inputEl');

// The native `autofocus` attribute only fires on initial page load, not when
// the input is revealed later via v-if (e.g. an inline rename field). Focus
// it explicitly on mount so blur-to-commit works when the user clicks away.
onMounted(() => {
  if (props.autofocus) {
    inputEl.value?.focus();
  }
});

function update(e: Event) {
  const raw = (e.target as HTMLInputElement).value;
  const value =
    props.type === 'number' ? (raw === '' ? null : Number(raw)) : raw;
  emit('update:modelValue', value);
}
</script>

<template>
  <UiField
    v-slot="{ fieldErrors }"
    :name="props.name"
    :label="props.label"
    :required="props.required"
    :error="props.error"
    :errors="props.errors"
    :class="{ 'field-with-icon': slots.icon }"
    :is-dark-background="props.isDarkBackground"
  >
    <div class="input-layout-container">
      <input
        ref="inputEl"
        :name="props.name"
        class="ui-input"
        :class="{
          'is-danger': fieldErrors.length > 0,
          'has-icon': slots.icon,
          'has-icon-right': slots.iconRight,
          disabled: props.disabled,
        }"
        :aria-invalid="fieldErrors.length > 0 || undefined"
        :type="type"
        :placeholder="props.placeholder"
        :value="props.modelValue"
        :disabled="props.disabled"
        :required="props.required"
        :maxlength="props.maxlength"
        :minlength="props.minlength"
        :min="props.min"
        :max="props.max"
        :step="props.step"
        @input="update"
        @focus="emit('focus')"
        @blur="emit('blur')"
        @keydown.enter="emit('enter')"
      />
      <div
        v-if="slots.icon"
        class="icon-left icon-size-sm"
        @click="emit('clickLeft')"
      >
        <slot name="icon" />
      </div>
      <div
        v-if="slots.iconRight"
        class="icon-right icon-size-sm"
        @click="emit('clickRight')"
      >
        <slot name="iconRight" />
      </div>
    </div>
  </UiField>
</template>

<style lang="scss" scoped>
.input-layout-container {
  position: relative;
}

input.ui-input {
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: stretch;
  padding: var(--space-8) var(--space-16);
  gap: var(--space-8);
  width: 100%;
  height: var(--control-height-lg);
  align-self: stretch;
  border: 1px solid var(--color-gray-300);
  background: #ffffff;
  box-shadow: var(--shadow-xs);
  border-radius: var(--border-radius-6);
}

input.ui-input.is-danger {
  border-color: var(--color-error-600);
}

.has-icon {
  padding-left: var(--space-40) !important;
}
.has-icon-right {
  padding-right: var(--space-40) !important;
}

.icon-left {
  color: var(--color-gray-400);
  position: absolute;
  top: px(12);
  left: px(12);
}

.icon-right {
  color: var(--color-gray-400);
  position: absolute;
  top: px(12);
  right: px(12);
}

.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.field-with-icon {
  position: relative;
}

::-webkit-input-placeholder {
  color: #ccc; /* Edge */
}
:-ms-input-placeholder {
  color: #ccc; /* Internet Explorer 10-11 */
}
::placeholder {
  color: #ccc;
}
</style>
